import { useQuery } from "@tanstack/react-query";
import type {
  DashboardOverview,
  DashboardLink,
  DashboardExpiringMedication,
} from "@shared/types/dashboard";
import { apiClient, ApiError } from "./client";

// Read-only aggregate counts for the dashboard "at a glance" row. Its own key
// so it refetches independently of the children/notifications queries the
// dashboard also loads (a slow/failed overview never blocks the profile grid).
const dashboardOverviewKey = ["dashboard", "overview"] as const;

export function useDashboardOverview() {
  return useQuery<DashboardOverview, ApiError>({
    queryKey: dashboardOverviewKey,
    queryFn: () => apiClient<DashboardOverview>("/api/dashboard/overview"),
  });
}

// Detail behind the stat tiles. Separate keys from the overview so opening a
// modal never invalidates the counts, and each is fetched ONLY while its modal
// is open — the dashboard must not get slower for people who never click. All
// sit under ["dashboard"], so a link cancel/delete refreshes the tile and the
// popup together (see useSharedLinks).
const linksKey = ["dashboard", "links"] as const;
const expiringMedicationsKey = ["dashboard", "expiring-medications"] as const;

/** The Active links popup: every link the parent has not deleted, with its status. */
export function useDashboardLinks(enabled: boolean) {
  return useQuery<DashboardLink[], ApiError>({
    queryKey: linksKey,
    queryFn: () => apiClient<DashboardLink[]>("/api/dashboard/links"),
    enabled,
  });
}

/** Only called while HEALTH_REMINDERS_ENABLED is on (the tile is hidden otherwise). */
export function useDashboardExpiringMedications(enabled: boolean) {
  return useQuery<DashboardExpiringMedication[], ApiError>({
    queryKey: expiringMedicationsKey,
    queryFn: () =>
      apiClient<DashboardExpiringMedication[]>("/api/dashboard/expiring-medications"),
    enabled,
  });
}
